import {useCallback, useEffect, useRef, useState} from 'react';

import type {TextAlign} from 'shells/typography/body';

import {DropdownButton} from '../button/dropdown-button/dropdown-button';
import {IconButton} from '../button/icon-button/icon-button';
import {CloseHeavyRegularIcon} from '../icon/svg/close-heavy-regular-icon';
import {SelectableListSingle} from '../selectable-list/selectable-list';
import type {FooterButtonProps} from '../selectable-list/selectable-list';

import styles from './dropdown-select-inline.module.css';

interface Option {
    id: string;
    name: string;
    type?: string;
}

interface Props {
    options: Option[];
    selectedValue: Option | null | undefined;
    onChange: (selected: Option | null) => void;
    onClear?: () => void;
    isDisabled?: boolean;
    emptyLabel?: string;
    footerButton?: FooterButtonProps | false;
    hasSearch?: boolean;
    isSmall?: boolean;
    selectedValueAlignment?: TextAlign;
}

export function DropdownSelectInline(props: Props) {
    const {hasSearch = true} = props;

    const [isOpen, setIsOpen] = useState<boolean>(false);

    const containerRef = useRef<HTMLDivElement | null>(null);
    const buttonRef = useRef<HTMLElement | null>(null);

    const handleMenuClose = useCallback(() => {
        setIsOpen(false);
        if (buttonRef.current) {
            buttonRef.current.blur();
        }
    }, []);

    useEffect(() => {
        if (!isOpen) return;

        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                handleMenuClose();
            }
        };

        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                handleMenuClose();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEscape);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscape);
        };
    }, [isOpen, handleMenuClose]);

    const handleSelect = (selectedId?: string | null) => {
        if (props.isDisabled) return;

        if (!selectedId) {
            if (props.onClear) {
                props.onClear();
            } else {
                props.onChange(null);
            }
            handleMenuClose();
            return;
        }

        const option = props.options.find((o) => o.id === selectedId);
        props.onChange(option ?? null);
        handleMenuClose();
    };

    const handleClear = () => {
        if (props.isDisabled) return;
        if (props.onClear) {
            props.onClear();
        } else {
            props.onChange(null);
        }
        setIsOpen(false);
    };

    const collectionWithType = props.options.map((o) => ({
        ...o,
        type: o.type ?? 'enum',
    }));

    return (
        <div ref={containerRef} className='pos-relative'>
            <div className={styles['trigger-row']}>
                <DropdownButton
                    label={props.selectedValue?.name ?? (props.emptyLabel || 'None')}
                    onClick={() => {
                        if (!props.isDisabled) setIsOpen((prev) => !prev);
                    }}
                    getButtonRef={(ref) => {
                        buttonRef.current = ref;
                    }}
                    isSmall={props.isSmall}
                    textAlign={props.selectedValueAlignment}
                />
                {props.selectedValue && props.onClear && (
                    <div className={styles['clear-button']}>
                        <IconButton
                            icon={CloseHeavyRegularIcon}
                            onClick={() => {
                                handleClear();
                            }}
                            tooltipText='Clear'
                            noPadding={true}
                            noHoverBackground={true}
                        />
                    </div>
                )}
            </div>
            {isOpen && (
                <div className={styles['menu-container']}>
                    {/* @ts-expect-error */}
                    <SelectableListSingle
                        collection={collectionWithType}
                        value={props.selectedValue?.id ?? null}
                        autoFocusSearch={true}
                        isNewStyle={true}
                        onSelect={handleSelect}
                        footerButton={props.footerButton || undefined}
                        hasSearch={hasSearch}
                    />
                </div>
            )}
        </div>
    );
}
